import React, { useEffect, useState } from 'react';
import { useIntl } from 'react-intl';
import BdcDemandApi, { InternalBdcAccount } from 'api/BdcDemandApi';
import Button from 'components/atoms/ui/Button';
import Loading from 'components/atoms/ui/Loading';
import { BUTTON_MAIN_TYPE } from 'constants/ui';
import { LOADER_TYPE } from 'constants/general';
import { toast } from 'react-toastify';
import { AxiosError } from 'axios';
import inputStyle from 'assets/style/common/Input.module.scss';
import accountStyle from 'assets/style/components/BDC/InternalBdcAccountPanel.module.scss';

const emptyAccount: InternalBdcAccount = {
  issuerName: '',
  issuerAddress: '',
  issuerSiret: '',
  issuerVatNumber: '',
  vatRate: 0,
  paymentBeneficiary: '',
  paymentIban: '',
  paymentBic: ''
};

const fields: Array<{ name: keyof InternalBdcAccount; label: string; type?: string }> = [
  { name: 'issuerName', label: 'bdc.account.issuerName' },
  { name: 'issuerAddress', label: 'bdc.account.issuerAddress' },
  { name: 'issuerSiret', label: 'bdc.account.issuerSiret' },
  { name: 'issuerVatNumber', label: 'bdc.account.issuerVatNumber' },
  { name: 'vatRate', label: 'bdc.account.vatRate', type: 'number' },
  { name: 'paymentBeneficiary', label: 'bdc.account.paymentBeneficiary' },
  { name: 'paymentIban', label: 'bdc.account.paymentIban' },
  { name: 'paymentBic', label: 'bdc.account.paymentBic' }
];

const InternalBdcAccountPanel = () => {
  const { formatMessage } = useIntl();
  const [account, setAccount] = useState<InternalBdcAccount>(emptyAccount);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [exists, setExists] = useState(false);
  const [loadFailed, setLoadFailed] = useState(false);
  const [invalidField, setInvalidField] = useState<keyof InternalBdcAccount | null>(null);

  useEffect(() => {
    let mounted = true;
    new BdcDemandApi().getInternalBdcAccount()
      .then(response => {
        if (!mounted) return;
        if (response.data) {
          setAccount(response.data);
          setExists(true);
        }
      })
      .catch(() => {
        if (mounted) {
          setLoadFailed(true);
          toast.error(formatMessage({ id: 'bdc.account.loadError' }));
        }
      })
      .finally(() => { if (mounted) setLoading(false); });
    return () => { mounted = false; };
  }, []);

  const save = async (event: React.FormEvent) => {
    event.preventDefault();
    if (saving || loadFailed) return;
    setInvalidField(null);
    setSaving(true);
    try {
      const response = await new BdcDemandApi().saveInternalBdcAccount({
        issuerName: account.issuerName,
        issuerAddress: account.issuerAddress,
        issuerSiret: account.issuerSiret,
        issuerVatNumber: account.issuerVatNumber,
        vatRate: Number(account.vatRate),
        paymentBeneficiary: account.paymentBeneficiary,
        paymentIban: account.paymentIban,
        paymentBic: account.paymentBic
      });
      setAccount(response.data);
      setExists(true);
      toast.success(formatMessage({ id: 'bdc.account.saved' }));
    } catch (error) {
      const response = (error as AxiosError<{ field?: string }>).response;
      const field = response?.status === 400
        ? fields.find(item => item.name === response.data?.field)
        : undefined;
      if (field) {
        setInvalidField(field.name);
        toast.error(formatMessage({ id: 'bdc.account.invalidField' }, {
          field: formatMessage({ id: field.label })
        }));
      } else if (response?.status === 403) {
        toast.error(formatMessage({ id: 'bdc.account.forbidden' }));
      } else {
        toast.error(formatMessage({ id: 'bdc.account.saveError' }));
      }
    } finally {
      setSaving(false);
    }
  };

  return (
    <section className={accountStyle.panel}>
      <h2>{formatMessage({ id: 'bdc.account.title' })}</h2>
      <p>{formatMessage({ id: 'bdc.account.description' })}</p>
      {!loading && !loadFailed && !exists && <p className={accountStyle.notice}>{formatMessage({ id: 'bdc.account.empty' })}</p>}
      {loading ? <Loading type={LOADER_TYPE.DROPZONE} /> : !loadFailed && (
        <form onSubmit={save}>
          <div className={accountStyle.fields}>
          {fields.map(field => (
            <div key={field.name} className={`${inputStyle.container} ${inputStyle.floating} ${accountStyle.field} ${account[field.name] !== '' ? inputStyle.hasValue : ''}`}>
              <label htmlFor={`bdc-account-${field.name}`}>{formatMessage({ id: field.label })}</label>
              <input
                id={`bdc-account-${field.name}`}
                name={field.name}
                type={field.type || 'text'}
                value={account[field.name] ?? ''}
                required
                min={field.name === 'vatRate' ? 0 : undefined}
                max={field.name === 'vatRate' ? 100 : undefined}
                step={field.name === 'vatRate' ? 0.01 : undefined}
                autoComplete="off"
                aria-invalid={invalidField === field.name}
                aria-describedby={invalidField === field.name ? `bdc-account-error-${field.name}` : undefined}
                onChange={event => {
                  const value = field.name === 'vatRate' ? Number(event.target.value) : event.target.value;
                  setAccount(current => ({ ...current, [field.name]: value }));
                  if (invalidField === field.name) setInvalidField(null);
                }}
              />
              {invalidField === field.name && <span id={`bdc-account-error-${field.name}`} className={inputStyle.errorRelative} role="alert">
                {formatMessage({ id: 'bdc.account.invalidField' }, { field: formatMessage({ id: field.label }) })}
              </span>}
            </div>
          ))}
          </div>
          <Button type={BUTTON_MAIN_TYPE.SECONDARY} role="submit" disabled={saving} loading={saving}>
            {formatMessage({ id: saving ? 'bdc.account.saving' : 'bdc.account.save' })}
          </Button>
        </form>
      )}
    </section>
  );
};

export default InternalBdcAccountPanel;
